import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { IMAGES } from "../lib/api";
import { Seo } from "../components/site/Seo";
import { Reveal } from "../components/site/Reveal";
import { ServiceHero, SectionHeading, CheckList } from "../components/site/ServiceHero";
import { CTABand } from "../components/site/CTABand";
import { LeadForm } from "../components/site/LeadForm";
import { HR_SERVICES, HR_PRICING, HR_STARTUP_INCLUDES, COMPLIANCE_CATEGORIES } from "../data/content";

export default function HRBackOffice() {
  return (
    <div data-testid="hr-page">
      <Seo title="Trucking HR & Back-Office Compliance Services" description="Organized driver files, documentation, compliance reminders, revenue tracking, and safety audit preparation for trucking companies. Plans starting at $200/month." />
      <ServiceHero eyebrow="HR & Back-Office" title="THE BUSINESS BEHIND" highlight="THE TRUCK MATTERS."
        subtitle="Running trucks is more than finding loads. Your company needs organized records, driver documentation, revenue tracking, policies, and compliance systems behind the scenes."
        image={IMAGES.dispatchOffice} />

      {/* Pricing */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Pricing" center className="mb-12">SIMPLE, SCALABLE <span className="nm-gold-text">PRICING.</span></SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {HR_PRICING.map((p, i) => (
              <Reveal key={p.tier} delay={i * 0.1}>
                <motion.div whileHover={{ y: -8 }}
                  className={`nm-card rounded-2xl p-8 text-center h-full ${i === 1 ? "border-[#D4AF37]/60 shadow-[0_20px_60px_-15px_rgba(212,175,55,0.3)]" : ""}`}
                  data-testid={`hr-price-${i}`}>
                  {i === 1 && <span className="inline-block mb-3 text-[10px] font-bold tracking-wider nm-gold-bg text-[#0B132B] px-3 py-1 rounded-full">POPULAR</span>}
                  <p className="font-mono text-xs tracking-wider text-gray-400 mb-3">{p.tier}</p>
                  <p className="font-head text-5xl font-black nm-gold-text">{p.price}</p>
                  <p className="text-gray-400 text-sm mt-1">{p.unit}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HR Start-Up Package */}
      <section className="relative py-20 overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Set it up right from day one" center className="mb-12">
            HR START-UP <span className="nm-gold-text">PACKAGE.</span>
          </SectionHeading>
          <Reveal>
            <div className="nm-card rounded-3xl overflow-hidden border-[#D4AF37]/40 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.3)]">
              <div className="nm-gold-bg p-8 text-center">
                <p className="font-mono text-xs tracking-[0.3em] text-[#0B132B]/80 mb-2">HR START-UP PACKAGE</p>
                <p className="font-head text-6xl font-black text-[#0B132B]">$500</p>
              </div>
              <div className="p-8 sm:p-10">
                <p className="text-gray-300 text-sm leading-relaxed mb-7 max-w-3xl">
                  Get your company's HR department set up the right way from the beginning. Our HR Start-Up Package helps trucking companies
                  establish a professional hiring, onboarding, payroll, and employee-management process.
                </p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {HR_STARTUP_INCLUDES.map((it, i) => (
                    <motion.div key={it} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                      className="flex items-start gap-3 text-gray-200 text-sm">
                      <Check size={17} className="text-[#10B981] mt-0.5 shrink-0" strokeWidth={3} />
                      {it}
                    </motion.div>
                  ))}
                </div>
                <div className="mt-8 rounded-2xl bg-[#2563EB]/10 border border-[#2563EB]/30 p-6">
                  <p className="text-gray-300 text-sm">
                    Perfect for new trucking companies or established companies that need a professional HR system put in place for their drivers and employees.
                  </p>
                </div>
                <a href="#hr-quote"
                  className="btn-sheen mt-7 inline-flex items-center justify-center gap-2 nm-gold-bg text-[#0B132B] font-bold px-7 py-3.5 rounded-full hover:scale-[1.02] transition-transform"
                  data-testid="hr-startup-cta">
                  GET THE HR START-UP PACKAGE
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Dashboard-style compliance */}
      <section className="relative py-20 bg-[#0B132B] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Always audit-ready" center className="mb-12">YOUR COMPLIANCE, <span className="nm-gold-text">ORGANIZED.</span></SectionHeading>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {COMPLIANCE_CATEGORIES.map((c, i) => (
              <motion.div key={c} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="nm-card rounded-xl p-5 flex items-center gap-3" data-testid={`compliance-${i}`}>
                <motion.span initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.1 + 0.2, type: "spring" }}
                  className="w-7 h-7 rounded-full bg-[#10B981] flex items-center justify-center shrink-0">
                  <Check size={16} className="text-white" strokeWidth={3} />
                </motion.span>
                <span className="font-head font-bold text-white text-sm">{c}</span>
              </motion.div>
            ))}
          </div>
          <Reveal>
            <p className="text-center font-head text-2xl sm:text-3xl font-extrabold text-white mt-14">
              DON'T WAIT UNTIL AN AUDIT <span className="nm-gold-text">TO GET ORGANIZED.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Services + form */}
      <section id="hr-quote" className="relative py-20 scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHeading eyebrow="What's included" className="mb-8">SERVICES</SectionHeading>
            <CheckList items={HR_SERVICES} />
          </div>
          <div className="lg:sticky lg:top-28">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Request a custom quote</p>
            <LeadForm source="hr" defaultService="HR & Back Office" submitLabel="REQUEST A CUSTOM QUOTE" />
          </div>
        </div>
      </section>

      <CTABand eyebrow="The business behind the truck matters" title="Get organized before you need to be." button="REQUEST A QUOTE" />
    </div>
  );
}
